import type { Me, Permission, Role } from "./types"

/**
 * UI mirror of the backend rules in apps/backend/src/modules/staff-access/permissions.ts
 * (canManageMember / canGrant / canMakeOwner / canStepDown / canTransferOwnership /
 * canGiveLogAccess). The UI only offers what the API will allow; the API still enforces every rule.
 */
export type Verdict = { ok: true } | { ok: false; reason: string }

const ok: Verdict = { ok: true }
const no = (reason: string): Verdict => ({ ok: false, reason })

export const MAX_OWNERS = 2

/** Shown on Users and permissions when the API doesn't send its own copy (same sentences as OWNER_RULES). */
export const OWNER_RULES = [
  `A store can have up to ${MAX_OWNERS} owners. Owners have every permission.`,
  `An owner can make another active staff member a co-owner while there are fewer than ${MAX_OWNERS} owners, after confirming their password (and two-step verification, if they use it).`,
  "Owners can't demote, deactivate or remove each other.",
  "An owner can step down to full permissions only while another owner remains.",
  "An owner can hand their own seat to someone else with Transfer ownership.",
  "Staff with full permissions can't add, change or remove owners.",
  "Every change to the owners is recorded in the logs, and all owners and staff with full permissions get a critical alert.",
]

export const LOG_ACCESS_RULES = [
  "Only one person can open the Store activity log and the Security log. Being an owner doesn't include it.",
  "Only the person who has log access can give it to someone else, after confirming their password. It moves to them; it isn't copied.",
  "Nobody can give log access to themselves.",
  "Every change is recorded in the security log, and all owners get a critical alert.",
]

export function canManageMember(me: Me, target: { id: string; role: Role }): Verdict {
  const s = me.staff
  if (!s) return no("No access.")
  if (target.id === s.id) return no("You can't change your own access. Ask a store owner.")
  if (target.role === "owner") {
    return s.role === "owner"
      ? no("Owners can't change, deactivate or remove another owner. Each owner can step down themselves while another owner remains.")
      : no("Only an owner can change the store owners, and only through Make co-owner, Step down or Transfer ownership.")
  }
  if (!s.permissions.includes("staff:write")) return no("You don't have permission to manage staff.")
  if (target.role === "admin" && s.role !== "owner") return no("Only a store owner can change staff with full permissions.")
  return ok
}

type Candidate = { id: string; role: Role; status: "active" | "deactivated" }

export function canMakeOwner(me: Me, target: Candidate, owners: number): Verdict {
  const s = me.staff
  if (!s || s.role !== "owner") return no("Only a store owner can make someone a co-owner.")
  if (target.id === s.id || target.role === "owner") return no("They're already a store owner.")
  if (target.status !== "active") return no("Reactivate this staff account before making them an owner.")
  if (owners >= MAX_OWNERS) return no(`A store can have at most ${MAX_OWNERS} owners. An owner has to step down first.`)
  return ok
}

export function canStepDown(me: Me, owners: number): Verdict {
  if (me.staff?.role !== "owner") return no("Only a store owner can step down.")
  if (owners < 2) return no("You're the only store owner. Make someone a co-owner or transfer ownership first.")
  return ok
}

export function canGiveLogAccess(me: Me, target: Candidate): Verdict {
  const s = me.staff
  if (!s?.log_viewer) return no("Only the person who has log access can give it to someone else.")
  if (target.id === s.id) return no("You already have log access.")
  if (target.status !== "active") return no("Reactivate this staff account before giving them log access.")
  return ok
}

/** Permissions the signed-in person may hand out; full permissions only for owners. */
export function grantable(me: Me): { full: boolean; permissions: Set<Permission> } {
  const s = me.staff
  if (!s || !s.permissions.includes("staff:write")) return { full: false, permissions: new Set() }
  return { full: s.role === "owner", permissions: new Set(s.permissions) }
}
